import _ from 'underscore';
import { useState } from 'react';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { HubLearnerSearchBar } from '~/clc/features/clc-hub-learner-search/components/HubLearnerSearchBar';
import { LogInButton } from '~/clc/features/learner-navigation/components/AcademyThemeNavigation/LogInButton';
import { ProfileLink } from '~/clc/features/learner-navigation/components/AcademyThemeNavigation/ProfileLink';
import { getExternalUser, I18n } from '~/common/explicit-exports';
import {
  AcademyNavigation,
  AcademyNavigationContentWrapper,
  BackToLink,
  BackToLinkSpacer,
  CustomLogo,
  DEFAULT_CUSTOM_LOGO_HEIGHT,
  SearchBarWrapper,
} from '~/clc/features/learner-navigation/components/AcademyThemeNavigation/AcademyThemeNavigation.styles';

const TRANSLATED_LOGO_LABEL = I18n.t('js.academy.navigation.logo_label');
const TRANSLATED_LOGIN = I18n.t('js.academy.navigation.login');
const TRANSLATED_OPENS_NEW_WINDOW = I18n.t('js.academy.navigation.opens_new_window');

export default function AcademyThemeNavigation({
  academy,
  academyContentTypesToLocalizedString,
  backToLink,
  backToLinkText,
  communityContentTypesToLocalizedString,
  contentWrapperStyle,
  mountedAcademyBanner,
  headerLinks,
  homepageLink,
  linkColor,
  linkHoverColor,
  logo,
  logoHeight,
  noBannerContent,
  showBackToLink,
  showSearch,
  subnavActiveLinkColor,
}) {
  const [searchVisible, setSearchVisible] = useState(false);
  const [mobileNavVisible, setMobileNavVisible] = useState(false);

  const navigationClasses = ['academy-navigation', ...(!logo ? ['no-custom-logo'] : [])];
  const externalUser = getExternalUser();
  const isLoggedIn = Boolean(externalUser);

  const showMobileSearchBar = () => {
    setSearchVisible(true);
    document.body.style.overflow = 'hidden';
  };

  const hideMobileSearchBar = () => {
    setSearchVisible(false);
    document.body.style.overflow = 'auto';
  };

  const hasNavOptions = !_.isEmpty(headerLinks) || !isLoggedIn;

  return (
    <AcademyNavigation
      className={navigationClasses.join(' ')}
      $linkColor={linkColor}
      $linkHoverColor={linkHoverColor}
      $mobileNavVisible={mobileNavVisible}
      $noBannerContent={noBannerContent}
      $searchVisible={searchVisible}
      $mountedAcademyBannerHeight={mountedAcademyBanner?.current?.clientHeight || 0}
      $isLoggedIn={isLoggedIn}
    >
      <AcademyNavigationContentWrapper
        className={`academy-navigation-content-wrapper ${academyCustomCSS.header.navigation}`}
        $hasNavOptions={hasNavOptions}
        style={contentWrapperStyle || {}}
      >
        {logo && (
          <a href={homepageLink || '/'} className={`academy-logo-link ${academyCustomCSS.header.logoLink}`}>
            <CustomLogo
              className="academy-logo"
              logoHeight={logoHeight || DEFAULT_CUSTOM_LOGO_HEIGHT}
              src={logo}
              alt={academy?.name || TRANSLATED_LOGO_LABEL}
            />
          </a>
        )}
        {hasNavOptions && (
          <nav
            className={`academy-nav ${
              mobileNavVisible ? academyCustomCSS.header.menuBackdropMobile : academyCustomCSS.header.customLinks
            }`}
          >
            <ul>
              {headerLinks.map(({ linkText, link, shouldOpenInNewWindow }) => (
                <li
                  id={`${linkText}-${link}`}
                  key={`${linkText}-${link}`}
                  className={academyCustomCSS.header.customLink}
                >
                  <a
                    href={link}
                    target={shouldOpenInNewWindow || _.isUndefined(shouldOpenInNewWindow) ? '_blank' : '_self'}
                    rel="noreferrer"
                  >
                    {linkText}
                  </a>
                </li>
              ))}
              {!isLoggedIn && (
                <li
                  id="login"
                  key="mobile-login"
                  className={`mobile-login-link ${academyCustomCSS.header.loginLinkMobile}`}
                >
                  <a href="/login">{TRANSLATED_LOGIN}</a>
                </li>
              )}
            </ul>
          </nav>
        )}
        {showBackToLink ? (
          <BackToLink
            className={academyCustomCSS.header.goBackToLink}
            href={backToLink}
            target="_blank"
            linkColor={linkColor}
            linkHoverColor={linkHoverColor}
          >
            <span>{backToLinkText}</span>
            <i className="fas fa-external-link-alt" aria-label={TRANSLATED_OPENS_NEW_WINDOW} />
          </BackToLink>
        ) : (
          <BackToLinkSpacer />
        )}
        {showSearch && (
          <SearchBarWrapper $searchVisible={searchVisible}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <i
              className={`fas fa-search ${academyCustomCSS.header.searchBarIconMobile}`}
              onClick={showMobileSearchBar}
            />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <i
              className={`fas fa-times ${academyCustomCSS.header.searchBarCloseIconMobile}`}
              onClick={hideMobileSearchBar}
            />
            <HubLearnerSearchBar
              className={`academy-search-bar-wrapper ${academyCustomCSS.header.searchBar}`}
              algoliaAppId={window.ENV_VARS.ALGOLIA_APP_ID}
              algoliaSearchApiKey={window.ALGOLIA_SEARCH_KEY}
              academyContentTypesToLocalizedString={academyContentTypesToLocalizedString}
              communityContentTypesToLocalizedString={communityContentTypesToLocalizedString}
              prioritizeAcademies
            />
          </SearchBarWrapper>
        )}
        {!isLoggedIn && <LogInButton linkColor={linkColor} linkHoverColor={linkHoverColor} />}
        {isLoggedIn && (
          <ProfileLink
            linkColor={linkColor}
            academy={academy}
            externalUser={externalUser}
            subnavActiveLinkColor={subnavActiveLinkColor}
          />
        )}
        {hasNavOptions && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <i
              className={`fas fa-bars ${academyCustomCSS.header.menuButtonMobile}`}
              onClick={() => setMobileNavVisible(true)}
            />
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <i
              className={`fas fa-times ${academyCustomCSS.header.closeMenuButtonMobile}`}
              onClick={() => setMobileNavVisible(false)}
            />
          </>
        )}
      </AcademyNavigationContentWrapper>
    </AcademyNavigation>
  );
}
